import React from 'react';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { usePagination } from '@plone/volto/helpers/Utils/usePagination';
import config from '@plone/volto/registry';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useDispatch, useSelector } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';

const defaultQuery = [
  {
    i: 'path',
    o: 'plone.app.querystring.operation.string.relativePath',
    v: '::1',
  },
];

const addQuery = [
  {
    i: 'path',
    o: 'plone.app.querystring.operation.string.path',
    v: '/undefined',
  },
];

const defaultData = {
  sort_on: 'getObjPositionInParent',
  sort_order: 'reverse',
};

export const addContentTypesToQuery = (dataQuery, contentTypes) => {
  return {
    ...dataQuery,
    query: [
      ...dataQuery.query,
      {
        i: 'portal_type',
        o: 'plone.app.querystring.operation.selection.any',
        v: contentTypes,
      },
    ],
  };
};

export const addMetadataToQuery = (dataQuery, metadata_fields) => {
  return {
    ...dataQuery,
    metadata_fields: metadata_fields,
  };
};

export const relativeQuery = ({ path, b_size }) => {
  const query = path.endsWith('/add') ? addQuery : defaultQuery;
  return {
    ...defaultData,
    b_size: b_size,
    query: query,
  };
};

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export function withQuerystringResults(WrappedComponent, getQuery) {
  function WithQuerystringResults(props) {
    const { path } = props;
    const { settings } = config;
    const b_size = settings.defaultPageSize;
    const block = props.id;
    // Se o conteúdo fornecer uma querystring para este bloco, dê preferência a ela.
    const blockQuerystring = props.content?.blocks?.[block]?.querystring;
    const baseQuery = getQuery({ path, b_size, props });
    const dataQuery = blockQuerystring
      ? { ...blockQuerystring, b_size: blockQuerystring.b_size ?? b_size }
      : baseQuery;
    const [initialPath] = React.useState(getBaseUrl(path));

    const { currentPage, setCurrentPage } = usePagination(dataQuery, 1);

    const querystringResults = useSelector(
      (state) => state.querystringsearch.subrequests,
    );
    const dispatch = useDispatch();

    const hasLoaded = !querystringResults?.[block]?.loading;
    const listingItems = querystringResults?.[block]?.items || [];

    const showAsQueryListing = querystringResults?.[block]?.total > b_size;
    const totalPages = showAsQueryListing
      ? Math.ceil(querystringResults[block].total / b_size)
      : 0;

    const prevBatch = showAsQueryListing
      ? querystringResults[block].batching?.prev
      : null;
    const nextBatch = showAsQueryListing
      ? querystringResults[block].batching?.next
      : null;

    useDeepCompareEffect(() => {
      dispatch(
        getQueryStringResults(initialPath, dataQuery, block, currentPage),
      );
    }, [block, initialPath, dataQuery, currentPage, dispatch]);

    return (
      <WrappedComponent
        {...props}
        onPaginationChange={(_e, { activePage }) => setCurrentPage(activePage)}
        total={querystringResults?.[block]?.total}
        batch_size={b_size}
        currentPage={currentPage}
        totalPages={totalPages}
        prevBatch={prevBatch}
        nextBatch={nextBatch}
        listingItems={listingItems}
        hasLoaded={hasLoaded}
      />
    );
  }

  WithQuerystringResults.displayName = `WithQuerystringResults(${getDisplayName(
    WrappedComponent,
  )})`;

  return hoistNonReactStatics(
    WithQuerystringResults,
    WrappedComponent,
    getQuery,
  );
}
